/**
 * The backoffice as a set of workspaces.
 *
 * The rail had grown to eight groups — products, marketing, ads, business,
 * visualizer, the shopping assistant, billing — and a vendor looking for one
 * page scanned all of them. Most sessions live in one of those areas, so the
 * rail now shows one area at a time, picked from the switcher at the top.
 *
 * Home is the one that looks across all of them: the combined dashboard and
 * the account. Every other workspace opens on its own overview, which is that
 * area's dashboard. Only the catalogue stays on every rail, because every area
 * works with products.
 *
 * Addresses follow the same shape: /{workspace}/{page}, and a page's tabs in
 * `?tab=`. So the first segment of any address says which workspace it is in,
 * and matching a page to its workspace is a prefix check. The catalogue,
 * shared by all of them, keeps /products.
 *
 * Pure, so the routing rules are tested without a router or a store.
 */

export const WORKSPACE_IDS = ['home', 'sales', 'visualizer', 'assistant', 'marketing', 'ads', 'business', 'brand'] as const;
export type WorkspaceId = (typeof WORKSPACE_IDS)[number];

export interface Workspace {
    id: WorkspaceId;
    labelKey: string;
    label: string;
    descriptionKey: string;
    description: string;
    /** Where choosing it takes you. */
    home: string;
    /**
     * Paths that belong to it. The rail follows the page: arriving at
     * /ads/campaigns from search, a notification or Ask AI switches to Ads, so
     * the page you are on is always in the rail you are looking at.
     */
    prefixes: string[];
    /**
     * How far to turn the aurora's hue, in degrees.
     *
     * The artwork is blue and cyan over cream, so Home keeps it as drawn and
     * the others rotate away from it far enough to be told apart at a glance:
     * indigo, green, violet, coral, amber. Rotation rather than six images, so
     * it animates between them and costs nothing to load.
     */
    hue: number;
}

export const WORKSPACES: Workspace[] = [
    {
        id: 'home',
        labelKey: 'WORKSPACE.HOME',
        label: 'Home',
        descriptionKey: 'WORKSPACE.HOME_DESC',
        description: 'What needs you today, across everything',
        home: '/home/dashboard',
        prefixes: ['/home'],
        hue: 0,
    },
    {
        // feat/room-to-revenue: the lead desk — buyers, what they asked for,
        // and the sales Ardealy influenced (docs/room-to-revenue.md).
        id: 'sales',
        labelKey: 'R2R.WORKSPACE.SALES',
        label: 'Sales',
        descriptionKey: 'R2R.WORKSPACE.SALES_DESC',
        description: 'Your buyers, what they asked for, and what they bought',
        home: '/sales/desk',
        prefixes: ['/sales'],
        hue: -110,
    },
    {
        id: 'visualizer',
        labelKey: 'WORKSPACE.VISUALIZER',
        label: 'Visualizer',
        descriptionKey: 'WORKSPACE.VISUALIZER_DESC',
        description: 'Your products in customers’ rooms, and who used it',
        home: '/visualizer/dashboard',
        prefixes: ['/visualizer'],
        hue: 45,
    },
    {
        id: 'assistant',
        labelKey: 'WORKSPACE.ASSISTANT',
        label: 'Shopping Assistant',
        descriptionKey: 'WORKSPACE.ASSISTANT_DESC',
        description: 'The AI chat on your product pages',
        home: '/assistant/dashboard',
        prefixes: ['/assistant'],
        hue: -70,
    },
    {
        id: 'marketing',
        labelKey: 'WORKSPACE.MARKETING',
        label: 'Marketing',
        descriptionKey: 'WORKSPACE.MARKETING_DESC',
        description: 'Brand, content, email and social posts',
        home: '/marketing/dashboard',
        prefixes: ['/marketing'],
        hue: 95,
    },
    {
        id: 'ads',
        labelKey: 'WORKSPACE.ADS',
        label: 'Ads',
        descriptionKey: 'WORKSPACE.ADS_DESC',
        description: 'Meta and Google campaigns, run from here',
        home: '/ads/dashboard',
        prefixes: ['/ads'],
        hue: 150,
    },
    {
        id: 'business',
        labelKey: 'WORKSPACE.BUSINESS',
        label: 'Business',
        descriptionKey: 'WORKSPACE.BUSINESS_DESC',
        description: 'Search presence, competitors and sales',
        home: '/business/dashboard',
        prefixes: ['/business'],
        hue: 205,
    },
];

/**
 * The Brand Portal (the Ardealy Network): a manufacturer's account reads what
 * shoppers do with its products across every retailer that sells them.
 *
 * Kept out of WORKSPACES, which is the retailer's set: a shop never sees it,
 * and a brand sees it with only the retailer workspaces it can use — see
 * `workspacesFor`.
 */
export const BRAND_WORKSPACE: Workspace = {
    id: 'brand',
    labelKey: 'BRAND_PORTAL.WORKSPACE',
    label: 'Brand',
    descriptionKey: 'BRAND_PORTAL.WORKSPACE_DESC',
    description: 'Demand for your products across every retailer',
    home: '/brand/overview',
    prefixes: ['/brand'],
    hue: 175,
};

/** Every workspace, the brand's included — for looking one up by id or path. */
export const ALL_WORKSPACES: Workspace[] = [...WORKSPACES, BRAND_WORKSPACE];

/**
 * The workspaces an account can use.
 *
 * A brand (PMSVendor.kind 'brand') gets the Brand Portal first, Marketing —
 * a manufacturer makes content and posts like a shop does, and the brand kit
 * and Content Studio work the same for it — and Home for its account, team and
 * billing. Sales, Visualizer, Shopping Assistant, Ads and Business are all fed
 * by a visualizer on the account's OWN website, which a manufacturer selling
 * through retailers does not have: on a brand account they would be empty
 * pages, so they are not offered (their addresses still open).
 */
export function workspacesFor(kind?: string | null): Workspace[] {
    if (kind === 'brand') return [BRAND_WORKSPACE, workspaceById('marketing'), workspaceById('home')];
    return WORKSPACES;
}

/**
 * The workspace to draw for a remembered choice: a brand falls back to the
 * Brand Portal, a retailer that wandered onto /brand falls back to Home.
 */
export function effectiveWorkspace(chosen: WorkspaceId, kind?: string | null): WorkspaceId {
    if (workspacesFor(kind).some((w) => w.id === chosen)) return chosen;
    return kind === 'brand' ? 'brand' : DEFAULT_WORKSPACE;
}

export const DEFAULT_WORKSPACE: WorkspaceId = 'home';

export const isWorkspaceId = (value: unknown): value is WorkspaceId =>
    typeof value === 'string' && (WORKSPACE_IDS as readonly string[]).includes(value);

export const workspaceById = (id: WorkspaceId): Workspace =>
    ALL_WORKSPACES.find((w) => w.id === id) ?? WORKSPACES[0];

/**
 * The workspace a page belongs to, or null for the pages every workspace
 * shares — the catalogue — which leave the current choice alone rather than
 * yanking the rail somewhere else.
 *
 * Matched on whole path segments: `/businesses` is not under `/business`.
 * Old addresses are not listed — they redirect, and the new one is matched.
 */
export function workspaceForPath(pathname: string): WorkspaceId | null {
    const path = (pathname || '/').split('?')[0];
    let best: { id: WorkspaceId; length: number } | null = null;

    for (const workspace of ALL_WORKSPACES) {
        for (const prefix of workspace.prefixes) {
            const matches = path === prefix || path.startsWith(`${prefix}/`);
            if (matches && (!best || prefix.length > best.length)) {
                best = { id: workspace.id, length: prefix.length };
            }
        }
    }

    return best?.id ?? null;
}

/**
 * The switcher's order once the vendor has said what they came for.
 *
 * Home first, then the chosen workspaces in the order they were picked, then
 * the rest under "More" — nothing is hidden, a vendor who changes their mind
 * finds everything one row lower. With no goals, everything is in one list
 * in the default order.
 */
export function orderWorkspaces(goals: readonly string[] = [], kind?: string | null): { focus: Workspace[]; more: Workspace[] } {
    // A brand's three workspaces are always in this order; its onboarding goals are a retailer's.
    if (kind === 'brand') return { focus: workspacesFor(kind), more: [] };
    // Sales — the buyers — stays beside Home whatever was picked: every goal ends there.
    const pinned: WorkspaceId[] = ['home', 'sales'];
    const chosen = goals.filter((g): g is WorkspaceId => isWorkspaceId(g) && !pinned.includes(g));
    if (!chosen.length) return { focus: WORKSPACES, more: [] };
    const unique = Array.from(new Set(chosen));
    return {
        focus: [...pinned.map(workspaceById), ...unique.map(workspaceById)],
        more: WORKSPACES.filter((w) => !pinned.includes(w.id) && !unique.includes(w.id)),
    };
}
